import React from 'react';
import { Phone, Mail } from 'lucide-react';
import logo from '../assets/botibonga_logo.png';

const productLinks = [
  { label: 'Home', href: '#home' },
  { label: 'Features', href: '#features' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Trust & Safety', href: '#trust' },
  { label: 'Pricing', href: '#', disabled: true },
];

const legalLinks = [
  { label: 'Privacy Policy', href: 'https://app.botibonga.com/privacy' },
  { label: 'Terms of Service', href: 'https://app.botibonga.com/terms' },
];

export default function Footer() {
  return (
    <footer
      id="contact"
      className="py-12 px-4 sm:px-6 lg:px-8"
      style={{ borderTop: '1px solid var(--border-footer)' }}
    >
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-10">
          {/* Brand column */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src={logo} alt="Botibonga Studio" className="w-8 h-8 rounded-lg" />
              <span className="font-semibold text-base" style={{ color: 'var(--text-heading)' }}>
                Botibonga Studio
              </span>
            </div>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>
              Create AI assistants for WhatsApp, no coding required. Help your business reply faster and
              never miss a customer.
            </p>
          </div>

          {/* Product links */}
          <div>
            <h4 className="font-semibold text-sm mb-4" style={{ color: 'var(--text-heading)' }}>
              Product
            </h4>
            <ul className="space-y-3">
              {productLinks.map((link, index) => (
                <li key={index}>
                  {link.disabled ? (
                    <span
                      className="text-sm flex items-center gap-2"
                      style={{ color: 'var(--text-disabled)' }}
                    >
                      {link.label}
                      <span
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{
                          background: 'var(--bg-accent-subtle)',
                          color: 'var(--text-accent)',
                        }}
                      >
                        Coming Soon
                      </span>
                    </span>
                  ) : (
                    <a
                      href={link.href}
                      className="text-sm transition-colors duration-200 hover:underline"
                      style={{ color: 'var(--text-body)' }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text-accent)')}
                      onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-body)')}
                    >
                      {link.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact info */}
          <div>
            <h4 className="font-semibold text-sm mb-4" style={{ color: 'var(--text-heading)' }}>
              Contact
            </h4>
            <ul className="space-y-3">
              <li>
                <a
                  href="tel:+254717811921"
                  className="text-sm flex items-center gap-2 transition-colors duration-200 hover:underline"
                  style={{ color: 'var(--text-body)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text-accent)')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-body)')}
                >
                  <Phone className="w-4 h-4 shrink-0" style={{ color: 'var(--text-accent-2)' }} />
                  +254 717 811 921
                </a>
              </li>
              <li>
                <a
                  href="mailto:sales@botibonga.com"
                  className="text-sm flex items-center gap-2 transition-colors duration-200 hover:underline"
                  style={{ color: 'var(--text-body)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text-accent)')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-body)')}
                >
                  <Mail className="w-4 h-4 shrink-0" style={{ color: 'var(--text-accent-2)' }} />
                  sales@botibonga.com
                </a>
              </li>
            </ul>
          </div>

          {/* Legal links */}
          <div>
            <h4 className="font-semibold text-sm mb-4" style={{ color: 'var(--text-heading)' }}>
              Legal
            </h4>
            <ul className="space-y-3">
              {legalLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm transition-colors duration-200 hover:underline"
                    style={{ color: 'var(--text-body)' }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text-accent)')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-body)')}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div
          className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-4"
          style={{ borderTop: '1px solid var(--border-footer)' }}
        >
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
            &copy; {new Date().getFullYear()} Botibonga Studio. All rights reserved.
          </p>

          <div className="flex items-center gap-3">
            <a
              href="https://www.instagram.com/botib0nga/"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg transition-colors duration-200"
              style={{ color: 'var(--text-muted)' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-accent)'; e.currentTarget.style.background = 'var(--bg-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'transparent'; }}
              aria-label="Instagram"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
              </svg>
            </a>
            <a
              href="https://linkedin.com/company/botibonga"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg transition-colors duration-200"
              style={{ color: 'var(--text-muted)' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-accent)'; e.currentTarget.style.background = 'var(--bg-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'transparent'; }}
              aria-label="LinkedIn"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
